import React from "react";
import {observer} from "mobx-react";
import {IconButton, Modal} from "@mui/material";
import {ModalCloseBlock, ModalContent} from "views/components/modals/common";
import CloseIcon from "@mui/icons-material/Close";
import {useViewController} from "data/services/locator";
import {
	IModalScoringController,
	ScrollSide,
} from "views/components/modals/modal_scoring/modal_scoring.controller";
import {Bindings} from "data/constants/bindings";
import styled from "@emotion/styled";

const SModalContent = styled(ModalContent)`
	max-height: 80vh;
	height: 100%;
	overflow: hidden;
`;

const Title = styled.h4`
	color: #ffffff;
	text-align: center;
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 140%; /* 28px */
`;

const Shadows = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;

	&:before,
	&:after {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 60px;
		content: "";
		pointer-events: none;
		background: linear-gradient(to bottom, black 0%, transparent);
		opacity: 0;
	}

	&:after {
		bottom: 0;
		top: auto;
		background: linear-gradient(to top, black 0%, transparent);
	}

	&.${ScrollSide.TOP} {
		&:before {
			opacity: 1;
		}
	}

	&.${ScrollSide.DOWN} {
		&:after {
			opacity: 1;
		}
	}

	&.${ScrollSide.BOTH} {
		&:after,
		&:before {
			opacity: 1;
		}
	}
`;

const ScoringContainer = styled.div`
	overflow: auto;
	height: 100%;
	width: 100%;
	border-radius: 4px 4px 0 0;
	-webkit-overflow-scrolling: touch;
	overflow-scrolling: touch;

	&::-webkit-scrollbar {
		width: 3px;
	}

	&::-webkit-scrollbar-track {
		background: var(--modalBackground);
	}

	&::-webkit-scrollbar-thumb {
		background-color: #636366;
		height: 3px;
	}
`;

const ScoringRow = styled.div`
	padding: 8px 16px;
	min-height: 40px;
	background: #000;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid var(--neutral300);
	position: relative;
	z-index: -1;

	&.heading {
		background: #3d434b;
		border-color: var(--neutral400);
	}

	span {
		color: #ffffff;
		font-size: 14px;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 19.6px */
	}

	p {
		color: #ffffff;
		text-align: right;
		font-size: 20px;
		font-style: normal;
		font-weight: 800;
		line-height: 140%; /* 28px */
	}
`;

export const ModalScoring: React.FC = observer(() => {
	const {
		i18n,
		isOpen,
		hittersScoringRows,
		pitchersScoringRows,
		close,
		scrollSide,
		onScroll,
		scrollRef,
	} = useViewController<IModalScoringController>(Bindings.ModalScoringController);

	return (
		<Modal open={isOpen}>
			<SModalContent>
				<ModalCloseBlock>
					<IconButton sx={{minWidth: 0}} onClick={close}>
						<CloseIcon color={"inherit"} />
					</IconButton>
				</ModalCloseBlock>
				<Title>{i18n.t("modal.scoring.title", "Scoring Points")}</Title>
				<Shadows className={`${scrollSide}`}>
					<ScoringContainer onScroll={(e) => onScroll(e.currentTarget)} ref={scrollRef}>
						<React.Fragment>
							<ScoringRow className="heading">
								<span>{i18n.t("scoring.hitters.name", "Hitters")}</span>
								<span>{i18n.t("scoring.hitters.points", "Points")}</span>
							</ScoringRow>
							{hittersScoringRows.map((row) => (
								<ScoringRow key={row.name}>
									<span>{row.name}</span>
									<p>{row.value}</p>
								</ScoringRow>
							))}
						</React.Fragment>
						<React.Fragment>
							<ScoringRow className="heading">
								<span>{i18n.t("scoring.pitcher.name", "Pitcher")}</span>
								<span>{i18n.t("scoring.pitcher.points", "Points")}</span>
							</ScoringRow>
							{pitchersScoringRows.map((row) => (
								<ScoringRow key={row.name}>
									<span>{row.name}</span>
									<p>{row.value}</p>
								</ScoringRow>
							))}
						</React.Fragment>
					</ScoringContainer>
				</Shadows>
			</SModalContent>
		</Modal>
	);
});
